import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Calendar, Users, DollarSign, Clock, CheckCircle, XCircle,
  Image, ChevronDown, Trash2, Plus, BarChart3, BookOpen, Camera, Lock,
  Star, MessageSquare, Edit2, Save, X, Package
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  confirmed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  completed: 'bg-green-500/20 text-green-400 border-green-500/30',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/30'
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-[#111] border border-white/10 rounded-xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-3 mb-2">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <span className="text-[#A1A1AA] text-sm">{label}</span>
    </div>
    <p className="text-2xl font-bold text-[#EDEDED]">{value}</p>
  </div>
);

const BookingsTab = ({ bookings, onStatusUpdate }) => {
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  const filtered = filter === 'all' ? bookings : bookings.filter(b => b.status === filter);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="booking-filters">
        {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-4 py-1.5 rounded-full text-sm capitalize transition-all ${
              filter === s ? 'gold-gradient-btn' : 'bg-[#1a1a1a] text-[#A1A1AA] border border-white/10 hover:text-white'
            }`}
            data-testid={`filter-${s}`}
          >
            {s} {s !== 'all' && `(${bookings.filter(b => b.status === s).length})`}
            {s === 'all' && `(${bookings.length})`}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-[#A1A1AA]">
          <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No bookings found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(booking => (
            <div
              key={booking.id}
              className="bg-[#111] border border-white/10 rounded-xl overflow-hidden"
              data-testid={`booking-${booking.id}`}
            >
              <div
                className="p-4 cursor-pointer flex items-center justify-between"
                onClick={() => setExpandedId(expandedId === booking.id ? null : booking.id)}
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[#EDEDED] font-medium">{booking.user_name}</span>
                      <span className="text-[#52525B]">-</span>
                      <span className="text-[#D4AF37] text-sm">{booking.event_type}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm text-[#A1A1AA]">
                      <span>{new Date(booking.event_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      <span className={`capitalize px-2 py-0.5 rounded-full text-xs border ${STATUS_COLORS[booking.status]}`}>
                        {booking.status}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[#D4AF37] font-bold">£{booking.total_price}</p>
                    <p className="text-xs text-[#A1A1AA] capitalize">{booking.package_tier}</p>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-[#A1A1AA] ml-3 transition-transform ${expandedId === booking.id ? 'rotate-180' : ''}`} />
              </div>

              {expandedId === booking.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  className="border-t border-white/10 p-4 bg-[#0a0a0a]"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-[#A1A1AA]">Email</p>
                      <p className="text-[#EDEDED]">{booking.user_email}</p>
                    </div>
                    <div>
                      <p className="text-[#A1A1AA]">Phone Number</p>
                      <p className="text-[#EDEDED]">{booking.phone_number || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-[#A1A1AA]">Event Time</p>
                      <p className="text-[#EDEDED]">{booking.event_time || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="text-[#A1A1AA]">Location</p>
                      <p className="text-[#EDEDED]">{booking.location || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="text-[#A1A1AA]">Venue</p>
                      <p className="text-[#EDEDED]">{booking.venue || 'Not specified'}</p>
                    </div>
                    {booking.saver_event_type && (
                      <div>
                        <p className="text-[#A1A1AA]">Saver Event Type</p>
                        <p className="text-[#EDEDED] capitalize">{booking.saver_event_type.replace(/_/g, ' ')}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-[#A1A1AA]">Expected Guests</p>
                      <p className="text-[#EDEDED]">{booking.expected_guests || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="text-[#A1A1AA]">Referral</p>
                      <p className="text-[#EDEDED]">{booking.referral_source || 'Not specified'}</p>
                    </div>
                    {booking.coverage_areas?.length > 0 && (
                      <div>
                        <p className="text-[#A1A1AA]">Coverage Areas</p>
                        <p className="text-[#EDEDED]">{booking.coverage_areas.join(', ')}</p>
                      </div>
                    )}
                    {booking.additional_services?.length > 0 && (
                      <div>
                        <p className="text-[#A1A1AA]">Additional Services</p>
                        <p className="text-[#EDEDED]">{booking.additional_services.join(', ')}</p>
                      </div>
                    )}
                    {booking.discount_percent > 0 && (
                      <div>
                        <p className="text-[#A1A1AA]">Discount</p>
                        <p className="text-[#D4AF37]">{booking.discount_percent}% off ({booking.discount_code})</p>
                      </div>
                    )}
                    {booking.notes && (
                      <div className="md:col-span-2">
                        <p className="text-[#A1A1AA]">Notes</p>
                        <p className="text-[#EDEDED]">{booking.notes}</p>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-[#A1A1AA] text-sm mb-2">Update Status:</p>
                    <div className="flex flex-wrap gap-2">
                      {['pending', 'confirmed', 'completed', 'cancelled'].map(status => (
                        <button
                          key={status}
                          onClick={() => onStatusUpdate(booking.id, status)}
                          disabled={booking.status === status}
                          className={`px-3 py-1.5 rounded-lg text-sm capitalize border transition-all ${
                            booking.status === status
                              ? STATUS_COLORS[status] + ' font-medium'
                              : 'bg-[#1a1a1a] text-[#A1A1AA] border-white/10 hover:text-white hover:border-white/20'
                          }`}
                          data-testid={`status-${status}-${booking.id}`}
                        >
                          {status}
                        </button>
                      ))}
                    </div>
                  </div>

                  <p className="text-xs text-[#52525B] mt-3">
                    Booked on: {new Date(booking.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </motion.div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PortfolioTab = ({ portfolio, onRefresh }) => {
  const [newImageUrl, setNewImageUrl] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const categories = portfolio?.categories || [];

  const addImage = async () => {
    if (!selectedCategory || !newImageUrl) {
      toast.error('Please select a category and enter an image URL');
      return;
    }
    try {
      await axios.post(`${API}/admin/portfolio/images`, {
        category: selectedCategory,
        image_url: newImageUrl
      });
      toast.success('Image added!');
      setNewImageUrl('');
      onRefresh();
    } catch (err) {
      toast.error('Failed to add image');
    }
  };

  const removeImage = async (category, imageUrl) => {
    try {
      await axios.delete(`${API}/admin/portfolio/images`, {
        data: { category, image_url: imageUrl }
      });
      toast.success('Image removed!');
      onRefresh();
    } catch (err) {
      toast.error('Failed to remove image');
    }
  };

  return (
    <div>
      <div className="bg-[#111] border border-white/10 rounded-xl p-5 mb-6" data-testid="add-image-form">
        <h3 className="text-[#EDEDED] font-medium mb-3 flex items-center gap-2">
          <Plus className="w-4 h-4 text-[#D4AF37]" /> Add Image to Portfolio
        </h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value)}
            className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm flex-shrink-0"
            data-testid="category-select"
          >
            <option value="">Select Category</option>
            {categories.map(c => (
              <option key={c.name} value={c.name}>{c.name}</option>
            ))}
          </select>
          <input
            type="text"
            value={newImageUrl}
            onChange={e => setNewImageUrl(e.target.value)}
            placeholder="Paste image URL..."
            className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm flex-1"
            data-testid="image-url-input"
          />
          <Button onClick={addImage} className="gold-gradient-btn rounded-lg text-sm" data-testid="add-image-btn">
            Add
          </Button>
        </div>
      </div>

      {categories.map(category => (
        <div key={category.name} className="mb-8" data-testid={`category-${category.name}`}>
          <h3 className="text-[#D4AF37] font-medium mb-3 flex items-center gap-2">
            <Camera className="w-4 h-4" />
            {category.name} ({category.images.length} photos)
          </h3>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {category.images.map((img, i) => (
              <div key={i} className="relative group aspect-square rounded-lg overflow-hidden bg-[#1a1a1a]">
                <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                <button
                  onClick={() => removeImage(category.name, img)}
                  className="absolute top-1 right-1 w-6 h-6 bg-red-600/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`remove-img-${i}`}
                >
                  <Trash2 className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const ReviewsTab = ({ onRefresh }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newReview, setNewReview] = useState({ user_name: '', rating: 5, comment: '', event_type: '' });

  const eventTypes = [
    { value: '', label: 'Select Event Type' },
    { value: 'portrait', label: 'Portrait' },
    { value: 'maternity', label: 'Maternity Shoot' },
    { value: 'baptism', label: 'Baptism' },
    { value: 'communion', label: 'Communion' },
    { value: 'confirmation', label: 'Confirmation' },
    { value: 'first_birthday', label: '1st Birthday' },
    { value: '21st_birthday', label: '21st Birthday' },
    { value: '50th_birthday', label: '50th Birthday' },
    { value: 'civil_wedding', label: 'Civil Wedding' },
    { value: 'silver_golden_wedding', label: 'Silver/Golden Wedding' },
    { value: 'house_warming', label: 'House Warming' },
    { value: 'other', label: 'Other' },
  ];

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/admin/reviews?type=photo`);
      setReviews(res.data);
    } catch (err) {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReviews(); }, []);

  const addReview = async () => {
    if (!newReview.user_name || !newReview.comment) {
      toast.error('Name and comment are required');
      return;
    }
    try {
      await axios.post(`${API}/admin/reviews?type=photo`, newReview);
      toast.success('Review added!');
      setNewReview({ user_name: '', rating: 5, comment: '', event_type: '' });
      setShowAddForm(false);
      fetchReviews();
      onRefresh();
    } catch (err) {
      toast.error('Failed to add review');
    }
  };

  const startEdit = (review) => {
    setEditingId(review.id);
    setEditForm({ user_name: review.user_name, rating: review.rating, comment: review.comment, event_type: review.event_type || '' });
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(`${API}/admin/reviews/${id}`, editForm);
      toast.success('Review updated!');
      setEditingId(null);
      fetchReviews();
    } catch (err) {
      toast.error('Failed to update review');
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`);
      toast.success('Review deleted');
      fetchReviews();
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete review');
    }
  };

  const renderStars = (rating, editable = false, onChange = null) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          className={`w-4 h-4 ${editable ? 'cursor-pointer' : ''} ${i <= rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-[#52525B]'}`}
          onClick={editable ? () => onChange(i) : undefined}
        />
      ))}
    </div>
  );

  if (loading) return <div className="text-center py-12 text-[#A1A1AA]">Loading reviews...</div>;

  return (
    <div>
      {/* Add Review Button */}
      <div className="mb-6">
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="gold-gradient-btn rounded-lg text-sm flex items-center gap-2"
          data-testid="add-review-toggle"
        >
          <Plus className="w-4 h-4" /> Add Review
        </Button>
      </div>

      {/* Add Review Form */}
      {showAddForm && (
        <div className="bg-[#111] border border-white/10 rounded-xl p-5 mb-6" data-testid="add-review-form">
          <h3 className="text-[#EDEDED] font-medium mb-4 flex items-center gap-2">
            <Plus className="w-4 h-4 text-[#D4AF37]" /> New Review
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              value={newReview.user_name}
              onChange={e => setNewReview({ ...newReview, user_name: e.target.value })}
              placeholder="Customer name"
              className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm"
              data-testid="new-review-name"
            />
            <select
              value={newReview.event_type}
              onChange={e => setNewReview({ ...newReview, event_type: e.target.value })}
              className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm"
              data-testid="new-review-event"
            >
              {eventTypes.map(et => <option key={et.value} value={et.value}>{et.label}</option>)}
            </select>
          </div>
          <div className="mt-3">
            <label className="text-sm text-[#A1A1AA] mb-1 block">Rating</label>
            {renderStars(newReview.rating, true, (r) => setNewReview({ ...newReview, rating: r }))}
          </div>
          <textarea
            value={newReview.comment}
            onChange={e => setNewReview({ ...newReview, comment: e.target.value })}
            placeholder="Review comment..."
            rows={3}
            className="mt-3 w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm"
            data-testid="new-review-comment"
          />
          <div className="flex gap-2 mt-4">
            <Button onClick={addReview} className="gold-gradient-btn rounded-lg text-sm" data-testid="save-new-review">Save Review</Button>
            <Button onClick={() => setShowAddForm(false)} variant="outline" className="rounded-lg text-sm border-white/10 text-[#A1A1AA]">Cancel</Button>
          </div>
        </div>
      )}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <div className="text-center py-16 text-[#A1A1AA]">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No reviews yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map(review => (
            <div key={review.id} className="bg-[#111] border border-white/10 rounded-xl p-4" data-testid={`review-item-${review.id}`}>
              {editingId === review.id ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={editForm.user_name}
                      onChange={e => setEditForm({ ...editForm, user_name: e.target.value })}
                      className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm"
                      data-testid="edit-review-name"
                    />
                    <select
                      value={editForm.event_type}
                      onChange={e => setEditForm({ ...editForm, event_type: e.target.value })}
                      className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm"
                      data-testid="edit-review-event"
                    >
                      {eventTypes.map(et => <option key={et.value} value={et.value}>{et.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-[#A1A1AA] mb-1 block">Rating</label>
                    {renderStars(editForm.rating, true, (r) => setEditForm({ ...editForm, rating: r }))}
                  </div>
                  <textarea
                    value={editForm.comment}
                    onChange={e => setEditForm({ ...editForm, comment: e.target.value })}
                    rows={2}
                    className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm"
                    data-testid="edit-review-comment"
                  />
                  <div className="flex gap-2">
                    <button onClick={() => saveEdit(review.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm bg-green-600/20 text-green-400 border border-green-500/30 hover:bg-green-600/30" data-testid="save-edit-review">
                      <Save className="w-3 h-3" /> Save
                    </button>
                    <button onClick={() => setEditingId(null)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm bg-[#1a1a1a] text-[#A1A1AA] border border-white/10 hover:text-white">
                      <X className="w-3 h-3" /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-[#EDEDED] font-medium">{review.user_name}</span>
                      {renderStars(review.rating)}
                      {review.event_type && (
                        <span className="text-xs text-[#D4AF37] bg-[#D4AF37]/10 px-2 py-0.5 rounded-full capitalize">
                          {review.event_type.replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                    <p className="text-[#A1A1AA] text-sm">{review.comment}</p>
                    <p className="text-[#52525B] text-xs mt-2">
                      {new Date(review.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => startEdit(review)}
                      className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#D4AF37] border border-white/10 transition-colors"
                      data-testid={`edit-review-${review.id}`}
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => deleteReview(review.id)}
                      className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#1a1a1a] text-[#A1A1AA] hover:text-red-400 border border-white/10 transition-colors"
                      data-testid={`delete-review-${review.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== Photo Packages Tab =====
const PhotoPackagesTab = () => {
  const [data, setData] = useState({ events: [], services_photo: [], services_video: [] });
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSvcId, setEditSvcId] = useState(null);
  const [svcForm, setSvcForm] = useState({});

  const fetchData = async () => {
    try { const res = await axios.get(`${API}/packages/photo`); setData(res.data); }
    catch { toast.error('Failed to load packages'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const TIER_LABELS = { starter: 'Starter', classic: 'Classic', elite: 'Elite' };

  const saveEventEdit = async (id) => {
    try {
      const tiers = {};
      for (const tier of Object.keys(editForm.tiers || {})) {
        const t = editForm.tiers[tier];
        tiers[tier] = {
          price: Number(t.price),
          features: typeof t.features === 'string' ? t.features.split('\n').filter(f => f.trim()) : t.features,
          description: t.description
        };
      }
      await axios.put(`${API}/admin/packages/photo/${id}`, { tiers });
      toast.success('Updated!');
      setEditingId(null);
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const saveSvcEdit = async (id) => {
    try {
      await axios.put(`${API}/admin/services/${id}`, { ...svcForm, price: Number(svcForm.price) });
      toast.success('Updated!');
      setEditSvcId(null);
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  if (loading) return <p className="text-center py-12 text-[#A1A1AA]">Loading...</p>;

  return (
    <div className="space-y-8">
      {/* Event Pricing */}
      <div>
        <h3 className="text-[#EDEDED] font-medium text-lg mb-4 flex items-center gap-2">
          <Camera className="w-5 h-5 text-[#D4AF37]" /> Event Pricing
        </h3>
        <div className="space-y-3">
          {data.events.map(event => (
            <div key={event.id} className="bg-[#111] border border-white/10 rounded-xl p-5">
              {editingId === event.id ? (
                <div className="space-y-4">
                  <p className="text-[#EDEDED] font-medium text-lg">{event.name}</p>
                  {Object.entries(editForm.tiers || {}).map(([tier, tierData]) => (
                    <div key={tier} className="border border-white/5 rounded-lg p-4 bg-[#0a0a0a]">
                      <p className="text-[#D4AF37] text-sm font-medium mb-2">{TIER_LABELS[tier] || tier}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-[#52525B]">Price (£)</label>
                          <input type="number" value={tierData.price} onChange={e => setEditForm({ ...editForm, tiers: { ...editForm.tiers, [tier]: { ...tierData, price: e.target.value } } })} className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm mt-1" />
                        </div>
                        <div>
                          <label className="text-xs text-[#52525B]">Description</label>
                          <input value={tierData.description} onChange={e => setEditForm({ ...editForm, tiers: { ...editForm.tiers, [tier]: { ...tierData, description: e.target.value } } })} className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm mt-1" />
                        </div>
                      </div>
                      <div className="mt-2">
                        <label className="text-xs text-[#52525B]">Features (one per line)</label>
                        <textarea value={typeof tierData.features === 'string' ? tierData.features : tierData.features?.join('\n')} onChange={e => setEditForm({ ...editForm, tiers: { ...editForm.tiers, [tier]: { ...tierData, features: e.target.value } } })} rows={3} className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm mt-1" />
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <button onClick={() => saveEventEdit(event.id)} className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm bg-green-600/20 text-green-400 border border-green-500/30"><Save className="w-3 h-3" /> Save</button>
                    <button onClick={() => setEditingId(null)} className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm bg-[#1a1a1a] text-[#A1A1AA] border border-white/10"><X className="w-3 h-3" /> Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-[#EDEDED] font-medium">{event.name}</h4>
                      <span className="text-xs text-[#52525B]">Coverage: {event.coverage}</span>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {Object.entries(event.tiers || {}).map(([tier, tierData]) => (
                        <div key={tier} className="bg-[#0a0a0a] border border-white/5 rounded-lg px-3 py-2">
                          <span className="text-xs text-[#D4AF37]">{TIER_LABELS[tier] || tier}</span>
                          <p className="text-[#EDEDED] font-bold">£{tierData.price}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <button onClick={() => { setEditingId(event.id); setEditForm({ tiers: JSON.parse(JSON.stringify(event.tiers)) }); }}
                    className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#D4AF37] border border-white/10"><Edit2 className="w-3.5 h-3.5" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Additional Services */}
      {[{ title: 'Photography Add-ons', items: data.services_photo }, { title: 'Video Add-ons', items: data.services_video }].map(section => (
        <div key={section.title}>
          <h3 className="text-[#EDEDED] font-medium text-lg mb-4">{section.title}</h3>
          <div className="space-y-2">
            {section.items.map(svc => (
              <div key={svc.id} className="bg-[#111] border border-white/10 rounded-xl p-4">
                {editSvcId === svc.id ? (
                  <div className="flex flex-col sm:flex-row gap-3 items-end">
                    <input value={svcForm.name} onChange={e => setSvcForm({ ...svcForm, name: e.target.value })} className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm flex-1" />
                    <input type="number" value={svcForm.price} onChange={e => setSvcForm({ ...svcForm, price: e.target.value })} className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm w-24" />
                    <button onClick={() => saveSvcEdit(svc.id)} className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm bg-green-600/20 text-green-400 border border-green-500/30"><Save className="w-3 h-3" /> Save</button>
                    <button onClick={() => setEditSvcId(null)} className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm bg-[#1a1a1a] text-[#A1A1AA] border border-white/10"><X className="w-3 h-3" /> Cancel</button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[#EDEDED] text-sm font-medium">{svc.name}</span>
                      <p className="text-[#52525B] text-xs">{svc.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#D4AF37] font-bold">£{svc.price}</span>
                      <button onClick={() => { setEditSvcId(svc.id); setSvcForm({ name: svc.name, price: svc.price, description: svc.description }); }}
                        className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#D4AF37] border border-white/10"><Edit2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function AdminDashboard() {
  const { user, isAdmin, loading, isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('bookings');
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);

  // Admin login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const userData = await login(email, password);
      if (!userData.is_admin) {
        setLoginError('This account does not have admin access.');
      }
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoginLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchData();
  }, [isAdmin]);

  const fetchData = async () => {
    setDataLoading(true);
    try {
      const [statsRes, bookingsRes, portfolioRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/bookings`),
        axios.get(`${API}/portfolio`)
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setPortfolio(portfolioRes.data);
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
      if (err.response?.status === 403) navigate('/login');
    } finally {
      setDataLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/status`, { status });
      toast.success(`Booking ${status}`);
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status } : b));
      // Refresh stats
      const statsRes = await axios.get(`${API}/admin/stats`);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="admin-login-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-[#0a0a0a] rounded-2xl p-8 border border-white/10">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h1 className="font-['Playfair_Display'] text-2xl text-[#EDEDED]">Admin Login</h1>
              <p className="text-[#A1A1AA] text-sm mt-1">Sign in to manage your business</p>
            </div>

            <form onSubmit={handleAdminLogin} className="space-y-5">
              <div>
                <label className="text-sm text-[#A1A1AA] block mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Enter admin email"
                  required
                  className="w-full bg-[#121212] border border-white/10 rounded-lg px-4 py-3 text-[#EDEDED] placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]/50"
                  data-testid="admin-login-email"
                />
              </div>
              <div>
                <label className="text-sm text-[#A1A1AA] block mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter password"
                    required
                    className="w-full bg-[#121212] border border-white/10 rounded-lg px-4 py-3 text-[#EDEDED] placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]/50 pr-12"
                    data-testid="admin-login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-[#A1A1AA]"
                  >
                    {showPassword ? <XCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {loginError && (
                <p className="text-red-400 text-sm" data-testid="admin-login-error">{loginError}</p>
              )}

              <Button
                type="submit"
                disabled={loginLoading}
                className="w-full gold-gradient-btn py-6"
                data-testid="admin-login-submit"
              >
                {loginLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  if (dataLoading) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
      </div>
    );
  }

  const tabs = [
    { id: 'bookings', label: 'Bookings', icon: Calendar },
    { id: 'portfolio', label: 'Portfolio', icon: Image },
    { id: 'reviews', label: 'Reviews', icon: MessageSquare },
    { id: 'packages', label: 'Packages', icon: Package },
  ];

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-['Playfair_Display'] text-[#EDEDED]">
            Admin <span className="gold-gradient-text">Dashboard</span>
          </h1>
          <p className="text-[#A1A1AA] text-sm mt-1">Manage your bookings, portfolio, and business</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stats-grid">
            <StatCard icon={Calendar} label="Total Bookings" value={stats.total_bookings} color="bg-[#D4AF37]/20 text-[#D4AF37]" />
            <StatCard icon={Clock} label="Pending" value={stats.pending} color="bg-yellow-500/20 text-yellow-400" />
            <StatCard icon={CheckCircle} label="Confirmed" value={stats.confirmed} color="bg-blue-500/20 text-blue-400" />
            <StatCard icon={DollarSign} label="Revenue" value={`£${stats.total_revenue}`} color="bg-green-500/20 text-green-400" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-white/10 pb-3" data-testid="admin-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
                activeTab === tab.id
                  ? 'gold-gradient-btn'
                  : 'text-[#A1A1AA] hover:text-white hover:bg-white/5'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'bookings' && (
          <BookingsTab bookings={bookings} onStatusUpdate={updateBookingStatus} />
        )}
        {activeTab === 'portfolio' && (
          <PortfolioTab portfolio={portfolio} onRefresh={fetchData} />
        )}
        {activeTab === 'reviews' && (
          <ReviewsTab onRefresh={fetchData} />
        )}
        {activeTab === 'packages' && (
          <PhotoPackagesTab />
        )}
      </div>
    </div>
  );
}
